import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { Wrench, ExternalLink } from "lucide-react";

/**
 * Full-screen gate that hides the public site when admin has turned on
 * Maintenance Mode. Admins can still reach /root/admin to turn it off.
 *
 * Admin-configurable from `Configuration → General Settings`:
 *   - maintenance_mode         (toggle)
 *   - maintenance_title        (heading shown big)
 *   - maintenance_message      (paragraph shown below)
 *   - maintenance_backup_url   (optional CTA → backup site)
 *   - maintenance_backup_label (CTA button label)
 */
export default function MaintenanceGate({ children }) {
  const { settings, loading } = useSiteSettings();

  // While loading public settings, render children (avoid a flash)
  if (loading) return children;
  if (!settings?.maintenance_mode) return children;

  // Bypass for admin routes (belt & suspenders — SiteRoutes doesn't mount for /root/admin/*)
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/root/admin")) {
    return children;
  }

  const siteName = settings?.site_name || "Maxlive88";
  const title = settings?.maintenance_title || "We'll be right back";
  const message = settings?.maintenance_message || "We are performing scheduled maintenance. Please check back shortly.";
  const backupUrl = (settings?.maintenance_backup_url || "").trim();
  const backupLabel = settings?.maintenance_backup_label || "Use backup site";

  return (
    <div
      data-testid="maintenance-gate"
      className="fixed inset-0 z-[9999] flex items-center justify-center px-6"
      style={{
        background:
          "radial-gradient(1200px 600px at 20% 10%, rgba(16,185,129,0.12), transparent 60%), radial-gradient(900px 500px at 90% 90%, rgba(245,158,11,0.10), transparent 60%), #070a12",
      }}
    >
      <div className="max-w-xl w-full bg-[#0b1220] border border-white/10 rounded-2xl p-8 sm:p-10 text-center shadow-2xl">
        <div className="mx-auto h-16 w-16 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center mb-5 animate-pulse">
          <Wrench className="text-amber-400" size={28} />
        </div>
        <div className="uppercase tracking-[0.25em] text-[11px] text-amber-300/90 mb-2">Maintenance · {siteName}</div>
        <h1 data-testid="maintenance-title" className="text-3xl sm:text-4xl font-bold text-white mb-3">{title}</h1>
        <p data-testid="maintenance-message" className="text-white/70 leading-relaxed whitespace-pre-line">{message}</p>

        {backupUrl && (
          <a
            href={backupUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="maintenance-backup-link"
            className="mt-7 inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm shadow-lg shadow-emerald-500/30 transition-colors"
          >
            <ExternalLink size={16} /> {backupLabel}
          </a>
        )}

        <div className="mt-8 flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider text-white/40">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Status: Scheduled
        </div>
      </div>
    </div>
  );
}
